import { useMemo } from 'react';
import { MapPinnedIcon } from 'lucide-react';
import { useYmaps, type YmapsBindings } from '@noor/maps';
import type { EntityCoordinates } from '@/entities/moderation';

interface RestaurantLocationMapProps {
  coords: EntityCoordinates | undefined;
  /** Overlay card text (the localized address line, or `—`). */
  addressLine: string;
}

/**
 * Location snapshot for the restaurant review modal (Figma `Локация` block):
 * a static Yandex snapshot centered on the branch pin at zoom 16 with the
 * address overlay card. Degrades to a neutral placeholder when the SDK is
 * disabled/loading or the branch has no coordinates — the address card stays.
 */
export function RestaurantLocationMap({
  coords,
  addressLine,
}: RestaurantLocationMapProps) {
  const { bindings, isDisabled } = useYmaps();
  if (!coords || isDisabled || !bindings) {
    return <MapPlaceholder addressLine={addressLine} />;
  }
  return (
    <LocationMapView bindings={bindings} coords={coords} addressLine={addressLine} />
  );
}

function LocationMapView({
  bindings,
  coords,
  addressLine,
}: {
  bindings: YmapsBindings;
  coords: EntityCoordinates;
  addressLine: string;
}) {
  const components = useMemo(() => bindings.reactify.module(bindings.ymaps3), [bindings]);
  const { YMap, YMapDefaultSchemeLayer, YMapDefaultFeaturesLayer, YMapMarker } =
    components;

  // The review modal re-renders as its queries resolve — the map props must
  // stay referentially stable or every render re-applies `location` to the
  // live map and re-attaches the marker, which crashes the SDK internals.
  // Keyed on the raw numbers because the coordinates object itself is rebuilt
  // per render upstream. No `useDefault`: this map never moves its camera
  // imperatively, so a stable plain object is the whole fix.
  const location = useMemo(
    () => ({ center: [coords.lng, coords.lat], zoom: 16 }),
    [coords.lat, coords.lng],
  );
  const markerCoordinates = useMemo(
    () => [coords.lng, coords.lat],
    [coords.lat, coords.lng],
  );

  if (!YMap) {
    return <MapPlaceholder addressLine={addressLine} />;
  }

  return (
    <div className="relative h-[179px] w-full overflow-hidden rounded-[20px] bg-[#f3f4f6] dark:bg-muted">
      <YMap location={location} mode="vector" style={{ width: '100%', height: '100%' }}>
        <YMapDefaultSchemeLayer />
        {YMapDefaultFeaturesLayer && <YMapDefaultFeaturesLayer />}
        <YMapMarker coordinates={markerCoordinates}>
          <span className="block -translate-x-1/2 -translate-y-1/2">
            <span className="block size-5 rounded-full border-[3px] border-white bg-primary shadow-md" />
          </span>
        </YMapMarker>
      </YMap>
      <AddressCard line={addressLine} />
    </div>
  );
}

function MapPlaceholder({ addressLine }: { addressLine: string }) {
  return (
    <div
      className="relative h-[179px] w-full overflow-hidden rounded-[20px] bg-[#f3f4f6] dark:bg-muted"
      data-testid="restaurant-location-placeholder"
    >
      <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-muted-foreground">
        <MapPinnedIcon className="size-8 opacity-50" />
      </div>
      <AddressCard line={addressLine} />
    </div>
  );
}

function AddressCard({ line }: { line: string }) {
  return (
    <div className="absolute top-3 left-3 max-w-[calc(100%-24px)] rounded-[8px] bg-white px-[14px] py-[7px] shadow-sm dark:bg-card">
      <p className="line-clamp-2 text-xs leading-normal font-medium break-words text-black dark:text-foreground">
        {line}
      </p>
    </div>
  );
}
